import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { resolveTextModule, getFacetDescription } from "../lib/textModules";
import { Button } from "../components/ui/button";
import { Download, RotateCcw, AlertTriangle, Compass, MessageSquare, Wrench, Info, Loader2 } from "lucide-react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/clarity-zones-logo.png";

const ZONE_COLORS = {
  Vision: "#D4A000",
  Alignment: "#5A68A5",
  "Deep Dive": "#22666E",
  Bridge: "#D97A40",
};

const STATUS_LABEL = { dominant: "dominant", balanced: "balanced", low: "low" };

const FIXED_INTRO = "Dieses Profil zeigt, wie Sie typischerweise kommunizieren und Informationen vermitteln. Es macht Präferenzen sichtbar, ohne Menschen auf einen festen Stil zu reduzieren. Besonders wirksam wird Kommunikation dann, wenn Sie Ihre bevorzugten Muster kennen und sie situativ ergänzen können.";

/* ── Collect all page CSS (Tailwind + print rules) for server-side rendering ── */
function collectPageCss() {
  const parts = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      parts.push(Array.from(sheet.cssRules).map((r) => r.cssText).join("\n"));
    } catch (e) {
      if (sheet.href) parts.push(`@import url("${sheet.href}");`);
    }
  }
  return parts.join("\n");
}

/* ── Inline an image so the server-side renderer needs no network access ── */
async function fetchAsDataUrl(url) {
  const blob = await (await fetch(url)).blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/* ── Page wrapper for print layout ── */
function ReportPage({ children, className = "" }) {
  return (
    <div className={`report-page ${className}`}>
      {children}
    </div>
  );
}

export default function ResultsPage() {
  const { t, lang } = useI18n();
  const { voucherSession } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const reportRef = useRef(null);

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staticBlocks, setStaticBlocks] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const answers = location.state?.answers;
  const userName = location.state?.name || "";

  useEffect(() => {
    if (!answers) {
      navigate("/");
      return;
    }
    const fetchResult = async () => {
      try {
        const [assessRes, blocksRes] = await Promise.all([
          axios.post(`${API}/assess`, { language: lang, answers, name: userName }),
          axios.get(`${API}/static-blocks`, { params: { lang } }),
        ]);
        setResult(assessRes.data);
        setStaticBlocks(blocksRes.data);
        if (voucherSession?.token) {
          try {
            await axios.post(`${API}/voucher/redeem`,
              { report_data: assessRes.data },
              { headers: { Authorization: `Bearer ${voucherSession.token}` } },
            );
          } catch (redeemErr) {
            if (process.env.NODE_ENV !== "production") {
              console.warn("Redeem skipped:", redeemErr.response?.data?.detail);
            }
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.error("Assessment error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, lang, userName, navigate, voucherSession?.token]);

  const handleDownloadPdf = async () => {
    if (!reportRef.current || pdfLoading) return;
    setPdfLoading(true);
    setPdfError(null);
    const title = userName ? `Clarity Zones - ${userName}` : "Clarity Zones Kommunikationsprofil";
    try {
      const logoDataUrl = await fetchAsDataUrl(LOGO_URL);
      const html = reportRef.current.outerHTML.split(`src="${LOGO_URL}"`).join(`src="${logoDataUrl}"`);
      const res = await axios.post(
        `${API}/report/pdf`,
        { html, css: collectPageCss(), title, lang },
        { headers: { Authorization: `Bearer ${voucherSession?.token}` }, responseType: "blob", timeout: 120000 },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      setPdfError(t("pdf_error"));
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-10 h-10 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-slate-500 font-medium">{t("preparing")}</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-slate-500">Error loading results.</p>
      </div>
    );
  }

  const { zones, ranking, pathos_logos, facets, text_modules } = result;

  const zoneDisplayOrder = ["Alignment", "Vision", "Deep Dive", "Bridge"];
  const barData = zoneDisplayOrder
    .filter((name) => zones[name])
    .map((name) => ({
      name: t(name.toLowerCase().replace(" ", "_")),
      fullName: name,
      score: zones[name].score,
      status: zones[name].status,
    }));

  const facetKeys = ["abstraktion", "ziel", "entscheid", "adressat", "struktur", "argument", "unsicherheit", "wirkung"];
  const radarData = facetKeys.map((fk) => ({
    facet: t(`facet_${fk}`),
    value: facets[fk]?.score || 0,
    fullMark: 100,
  }));

  const primaryText = resolveTextModule(text_modules.primary_text_id, {
    ZONE: t(ranking.primary_zone.toLowerCase().replace(" ", "_")),
  });
  const secondaryText = text_modules.secondary_text_id
    ? resolveTextModule(text_modules.secondary_text_id, {
        ZONE: t(ranking.secondary_zone.toLowerCase().replace(" ", "_")),
      })
    : null;
  const effectText = resolveTextModule(text_modules.effect_text_id);
  const applicationText = resolveTextModule(text_modules.application_text_id);
  const zoneInsights = text_modules.zone_text_ids.map((id) => resolveTextModule(id)).filter(Boolean);
  const bullets = text_modules.bullet_ids.map((id) => resolveTextModule(id)).filter(Boolean);
  const pathosLogosKey =
    pathos_logos.effect_type === "rational"
      ? `${lang}_logos_dominant`
      : pathos_logos.effect_type === "emotional"
      ? `${lang}_pathos_dominant`
      : `${lang}_balanced_logos_pathos`;
  const pathosLogosText = resolveTextModule(pathosLogosKey);
  const ethosText = resolveTextModule(`${lang}_ethos_note`);
  const zoneKeyMap = { Vision: "vision", Alignment: "alignment", "Deep Dive": "deepdive", Bridge: "bridge" };

  const handleBarClick = (data) => {
    const fullName = data?.activePayload?.[0]?.payload?.fullName;
    if (!fullName) return;
    setSelectedZone((prev) => (prev === fullName ? null : fullName));
  };

  const getZoneDetail = (zoneName) => {
    if (!staticBlocks?.zone_legend) return null;
    return staticBlocks.zone_legend[zoneKeyMap[zoneName]];
  };

  return (
    <div className="min-h-screen bg-white" data-testid="results-page">
      {/* Action bar — screen only */}
      <div className="no-print fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-100">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-3">
          <img src={LOGO_URL} alt="Clarity Zones" className="h-8 w-auto" data-testid="report-header-logo" />
          <div className="flex gap-3">
            <Button
              data-testid="download-pdf-button"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm px-6 py-2 h-auto font-bold tracking-wide text-sm shadow-lg hover:-translate-y-0.5 transition-all duration-200 gap-2 disabled:opacity-70"
            >
              {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {pdfLoading ? t("pdf_generating") : t("download_pdf")}
            </Button>
            <Button
              data-testid="restart-button"
              variant="outline"
              onClick={() => navigate("/")}
              className="border-slate-200 hover:bg-slate-50 rounded-sm px-4 py-2 h-auto text-sm gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              {t("restart")}
            </Button>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div ref={reportRef} className="report-container max-w-5xl mx-auto px-6 pt-24 pb-20">

        {/* Warning banners — screen only */}
        {pdfError && (
          <div className="no-print mb-4 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="pdf-error">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 leading-relaxed">{pdfError}</p>
          </div>
        )}
        <div className="no-print mb-4 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 leading-relaxed">{t("download_hint")}</p>
        </div>
        {voucherSession && (
          <div className="no-print mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 leading-relaxed">{t("voucher_onetime_notice")}</p>
          </div>
        )}

        {/* ═══════════════ PAGE 1: Title + Bar Chart ═══════════════ */}
        <ReportPage>
          <div className="mb-6">
            <img src={LOGO_URL} alt="Clarity Zones" className="h-12 w-auto" data-testid="report-logo" />
          </div>

          {userName && (
            <p className="font-mono text-sm text-slate-400 tracking-widest mb-2">{userName.toUpperCase()}</p>
          )}
          <h1 className="font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 break-words" data-testid="results-title">
            Ihr Clarity Zones Kommunikationsprofil
          </h1>

          {/* Fixed intro text */}
          <p className="mt-6 text-sm text-slate-500 leading-relaxed">{FIXED_INTRO}</p>

          {/* Primary/Secondary zone */}
          <div className="mt-6 space-y-2">
            {primaryText && <p className="text-base text-slate-700 font-medium" data-testid="primary-text">{primaryText}</p>}
            {secondaryText && <p className="text-sm text-slate-500" data-testid="secondary-text">{secondaryText}</p>}
            {effectText && <p className="text-sm text-slate-500 italic" data-testid="effect-text">{effectText}</p>}
          </div>

          {/* Bar Chart */}
          <div className="mt-8 bg-white border border-slate-100 rounded-lg p-6" data-testid="zone-chart">
            <h2 className="font-bold text-base tracking-tight text-slate-900 mb-4">{t("zones_title")}</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 30 }} onClick={handleBarClick} style={{ cursor: "pointer" }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" domain={[0, 60]} tick={{ fontSize: 11, fill: "#94A3B8" }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: "#0F172A", fontWeight: 600 }} width={85} />
                <Tooltip
                  contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", fontSize: "12px" }}
                  formatter={(val, _name, props) => [`${val.toFixed(1)} (${t(STATUS_LABEL[props.payload.status])})`, t("score_label")]}
                />
                <Bar dataKey="score" radius={[0, 6, 6, 0]} barSize={24}>
                  {barData.map((entry) => (
                    <Cell key={entry.fullName} fill={ZONE_COLORS[entry.fullName]} stroke={selectedZone === entry.fullName ? "#0F172A" : "none"} strokeWidth={selectedZone === entry.fullName ? 2 : 0} style={{ cursor: "pointer" }} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {barData.map((z) => (
                <button key={z.fullName} onClick={() => setSelectedZone((prev) => (prev === z.fullName ? null : z.fullName))} className={`flex items-center gap-2 text-left rounded-md px-2 py-1 transition-all duration-200 ${selectedZone === z.fullName ? "bg-slate-100 ring-1 ring-slate-300" : "hover:bg-slate-50"}`} data-testid={`zone-label-${z.fullName.toLowerCase().replace(" ", "-")}`}>
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: ZONE_COLORS[z.fullName] }} />
                  <span className="text-xs text-slate-500">{z.name}</span>
                  <span className="font-mono text-xs font-bold text-slate-900 ml-auto">{z.score.toFixed(1)}</span>
                </button>
              ))}
            </div>
            {/* Interactive zone detail — screen only */}
            {selectedZone && getZoneDetail(selectedZone) && (
              <div className="mt-4 p-4 rounded-lg border no-print" style={{ borderColor: ZONE_COLORS[selectedZone] + "40", backgroundColor: ZONE_COLORS[selectedZone] + "08" }} data-testid="zone-detail-popover">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-bold text-sm text-slate-900">{getZoneDetail(selectedZone).title}</h4>
                    <p className="text-xs text-slate-400">{getZoneDetail(selectedZone).subtitle}</p>
                  </div>
                  <button onClick={() => setSelectedZone(null)} className="text-slate-400 hover:text-slate-600 text-xs">✕</button>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-2">{getZoneDetail(selectedZone).description}</p>
                <p className="text-xs text-slate-500">{getZoneDetail(selectedZone).focus}</p>
              </div>
            )}
          </div>
        </ReportPage>

        {/* ═══════════════ PAGE 2: Radar + Zone Insights ═══════════════ */}
        <ReportPage>
          {/* Radar Chart */}
          <div className="bg-white border border-slate-100 rounded-lg p-6 mb-8" data-testid="facet-chart">
            <h2 className="font-bold text-base tracking-tight text-slate-900 mb-4">{t("facets_title")}</h2>
            <div className="flex justify-center">
              <RadarChart width={620} height={400} data={radarData} cx={310} cy={200} outerRadius={135} data-testid="facet-radar">
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="facet" tick={{ fontSize: 11, fill: "#64748B" }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: "#94A3B8" }} />
                <Radar name="Score" dataKey="value" stroke="#0F172A" fill="#0F172A" fillOpacity={0.12} strokeWidth={2} />
              </RadarChart>
            </div>
          </div>

          {/* Zone Insights */}
          {zoneInsights.length > 0 && (
            <div data-testid="zone-insights">
              <h2 className="font-bold text-base tracking-tight text-slate-900 mb-3">{t("zone_insights_title")}</h2>
              <div className="space-y-2">
                {zoneInsights.map((text, i) => (
                  <div key={`zone-insight-${i}-${text.slice(0,20)}`} className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 leading-relaxed">{text}</div>
                ))}
              </div>
            </div>
          )}
        </ReportPage>

        {/* ═══════════════ PAGE 3: Facet Details ═══════════════ */}
        <ReportPage>
          <h2 className="font-bold text-base tracking-tight text-slate-900 mb-5" data-testid="facet-details">{t("facets_title")} — Detail</h2>
          <div className="space-y-3">
            {facetKeys.map((fk) => {
              const facet = facets[fk];
              if (!facet) return null;
              const desc = getFacetDescription(lang, fk, facet.level);
              return (
                <div key={fk} className="bg-white border border-slate-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm text-slate-900">{t(`facet_${fk}`)}</span>
                    <span className="font-mono text-xs text-slate-500">{facet.score.toFixed(1)} — {t(`facet_${facet.level}`)}</span>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                      <span>{t(`facet_left_${fk}`)}</span>
                      <span>{t(`facet_right_${fk}`)}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full" style={{ position: "relative" }}>
                      <div className="w-4 h-4 bg-slate-900 rounded-full border-2 border-white shadow-md" style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", left: `calc(${Math.min(100, Math.max(0, facet.score))}% - 8px)` }} />
                    </div>
                  </div>
                  {desc && <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>}
                </div>
              );
            })}
          </div>
        </ReportPage>

        {/* ═══════════════ PAGE 4: Wirkungsstil + Application + Bullets + Ethos ═══════════════ */}
        <ReportPage>
          {/* Pathos / Logos */}
          <div className="mb-8" data-testid="pathos-logos-section">
            <h2 className="font-bold text-base tracking-tight text-slate-900 mb-3">{t("pathos_logos_title")}</h2>
            <div className="bg-white border border-slate-100 rounded-lg p-5">
              <div className="flex items-center gap-6 mb-3">
                <div className="text-center">
                  <p className="font-mono text-xl font-bold text-slate-900">{pathos_logos.pathos.toFixed(1)}</p>
                  <p className="text-xs text-slate-500 mt-1">{t("pathos")}</p>
                </div>
                <div className="flex-1 h-3 bg-slate-100 rounded-full" style={{ position: "relative", overflow: "hidden" }}>
                  <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${(pathos_logos.pathos / (pathos_logos.pathos + pathos_logos.logos)) * 100}%` }} />
                </div>
                <div className="text-center">
                  <p className="font-mono text-xl font-bold text-slate-900">{pathos_logos.logos.toFixed(1)}</p>
                  <p className="text-xs text-slate-500 mt-1">{t("logos")}</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{pathosLogosText}</p>
            </div>
          </div>

          {/* Application */}
          {applicationText && (
            <div className="mb-8" data-testid="application-section">
              <h2 className="font-bold text-base tracking-tight text-slate-900 mb-3">{t("application_title")}</h2>
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-sm text-slate-700 leading-relaxed">{applicationText}</p>
              </div>
            </div>
          )}

          {/* Bullets */}
          {bullets.length > 0 && (
            <div className="mb-8" data-testid="bullets-section">
              <h2 className="font-bold text-base tracking-tight text-slate-900 mb-3">{t("bullet_title")}</h2>
              <ul className="space-y-2">
                {bullets.map((b, i) => (
                  <li key={`bullet-${i}-${b.slice(0,15)}`} className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Ethos */}
          {ethosText && (
            <div data-testid="ethos-section">
              <h2 className="font-bold text-base tracking-tight text-slate-900 mb-3">{t("ethos_title")}</h2>
              <div className="p-4 border-l-4 border-slate-300 bg-slate-50 rounded-r-lg">
                <p className="text-sm text-slate-600 leading-relaxed italic">{ethosText}</p>
              </div>
            </div>
          )}
        </ReportPage>

        {/* ═══════════════ PAGE 5: Zone Legend ═══════════════ */}
        {staticBlocks?.zone_legend && (
          <ReportPage>
            <div className="flex items-center gap-3 mb-3" data-testid="zone-legend-section">
              <Compass className="w-5 h-5 text-slate-700" />
              <h2 className="font-bold text-base tracking-tight text-slate-900">{staticBlocks.zone_legend.title}</h2>
            </div>
            <p className="text-sm text-slate-500 leading-relaxed mb-5">{staticBlocks.zone_legend.intro}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {["alignment", "vision", "deepdive", "bridge"].map((zk) => {
                const zone = staticBlocks.zone_legend[zk];
                if (!zone) return null;
                const colorMap = { vision: "#D4A000", alignment: "#5A68A5", deepdive: "#22666E", bridge: "#D97A40" };
                return (
                  <div key={zk} className="bg-white border border-slate-100 rounded-lg p-4" style={{ borderLeft: `4px solid ${colorMap[zk]}` }} data-testid={`zone-legend-${zk}`}>
                    <h3 className="font-bold text-sm text-slate-900 mb-1">{zone.title}</h3>
                    <p className="text-xs text-slate-400 mb-2">{zone.subtitle}</p>
                    <p className="text-sm text-slate-600 leading-relaxed mb-2">{zone.description}</p>
                    <p className="text-xs text-slate-500 mb-1">{zone.focus}</p>
                    <p className="text-xs text-slate-400 italic mb-2">{zone.rhetoric_mix}</p>
                    {zone.examples?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {zone.examples.map((ex, i) => (
                          <span key={`${zk}-ex-${i}`} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">{ex}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ReportPage>
        )}

        {/* ═══════════════ PAGE 6: Quick Check + Troubleshooting + Context Help ═══════════════ */}
        {staticBlocks?.quick_check && (
          <ReportPage>
            <div className="mb-10" data-testid="quick-check-section">
              <div className="flex items-center gap-3 mb-3">
                <MessageSquare className="w-5 h-5 text-slate-700" />
                <h2 className="font-bold text-base tracking-tight text-slate-900">{staticBlocks.quick_check.title}</h2>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <p className="text-sm text-slate-500 leading-relaxed mb-3">{staticBlocks.quick_check.intro}</p>
                <ol className="space-y-2.5">
                  {staticBlocks.quick_check.items?.map((item, i) => (
                    <li key={`qc-${i}`} className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed">
                      <svg width="24" height="24" viewBox="0 0 24 24" className="flex-shrink-0 mt-0.5">
                        <circle cx="12" cy="12" r="11" fill="white" stroke="#e2e8f0" strokeWidth="1" />
                        <text x="12" y="12" textAnchor="middle" dominantBaseline="central" fill="#94a3b8" fontSize="11" fontFamily="JetBrains Mono, monospace" fontWeight="bold">{i + 1}</text>
                      </svg>
                      {item}
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {staticBlocks?.troubleshooting && (
              <div className="mb-10" data-testid="troubleshooting-section">
                <div className="flex items-center gap-3 mb-3">
                  <Wrench className="w-5 h-5 text-slate-700" />
                  <h2 className="font-bold text-base tracking-tight text-slate-900">{staticBlocks.troubleshooting.title}</h2>
                </div>
                <p className="text-sm text-slate-500 leading-relaxed mb-3">{staticBlocks.troubleshooting.intro}</p>
                <div className="space-y-2">
                  {staticBlocks.troubleshooting.items?.map((item, i) => (
                    <div key={`ts-${item.zone}-${i}`} className="bg-white border border-slate-100 rounded-lg p-4" data-testid={`troubleshooting-item-${i}`}>
                      <p className="text-sm font-semibold text-slate-900 mb-0.5">{item.problem}</p>
                      <p className="text-xs font-mono text-indigo-600 mb-1">{item.zone}</p>
                      <p className="text-sm text-slate-600 leading-relaxed">{item.recommendation}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {staticBlocks?.report_context_help && (
              <div data-testid="report-context-help-section">
                <div className="flex items-center gap-3 mb-3">
                  <Info className="w-5 h-5 text-slate-700" />
                  <h2 className="font-bold text-base tracking-tight text-slate-900">{staticBlocks.report_context_help.title}</h2>
                </div>
                <div className="p-4 border-l-4 border-sky-300 bg-sky-50 rounded-r-lg">
                  <p className="text-sm text-slate-600 leading-relaxed mb-3">{staticBlocks.report_context_help.intro}</p>
                  <ul className="space-y-2">
                    {staticBlocks.report_context_help.notes?.map((note, i) => (
                      <li key={`rch-${i}`} className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
                        <span className="w-1.5 h-1.5 rounded-full bg-sky-400 mt-2 flex-shrink-0" />
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </ReportPage>
        )}

      </div>
    </div>
  );
}
